/**
 * ExperimentsProvider — A/B testing infrastructure powered by Firebase
 * Remote Config + Firebase A/B Testing.
 *
 * Why Firebase RC + A/B Testing (and not a third-party SaaS): the engine is
 * native to our existing stack — same Firebase project, same GA4 hookup, same
 * Auth identity. No extra account, no SaaS subscription, no cross-origin SDK
 * to ship.
 *
 * Behaviour:
 *   - On app start (and once on auth state change), call `fetchAndActivate`
 *     against Remote Config. The fetch is short-circuited inside
 *     `loadFeatures` if RC is not supported (e.g. SSR / very old browsers).
 *   - Defaults are configured in code so consumers reading via `getValue`
 *     never observe `undefined`, even before the network roundtrip succeeds.
 *   - Each assignment is logged once per `(experimentId, variant)` pair to
 *     our existing Firestore `analyticsEvents` pipeline through `trackEvent`
 *     (event name: `experiment_assignment`). Firebase A/B Testing also picks
 *     up the activation natively via the GA4 user property the SDK sets on
 *     activate, so the conversion stats appear in the Firebase Console without
 *     any extra wiring on our side.
 *   - Anonymous bucketing is handled by Firebase Installation IDs (free,
 *     anonymous). Logged-in users are additionally attributed via the GA4
 *     `firebase_user_id` user property which Firebase Auth sets automatically.
 *     We don't have to sync attributes manually like with GrowthBook.
 *
 * If Remote Config is not supported in the current environment (or the fetch
 * fails), the provider still mounts and consumers fall back to in-code
 * defaults — every `useExperiment` call returns the first variant
 * (by convention, `control`). The app stays safe to run before any
 * experiment is provisioned in the Firebase console.
 */
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
// NOTE: `firebase/remote-config` is imported **dynamically** inside the init
// effect (see below), NOT statically. This provider is mounted eagerly at app
// boot (src/app.tsx), so a static import would drag the whole Remote Config
// SDK (~20-40KB) plus its network fetch into the initial boot chunk — even
// though experiments are never read on first paint (defaults cover the
// loading state). The only thing we import statically is the *type*, which is
// fully erased at compile time and adds nothing to the bundle.
import type { RemoteConfig } from 'firebase/remote-config';
import { app } from '../firebase';

// `import('...')` type queries are purely type-level (fully erased at compile
// time) so referencing the SDK's function signatures here costs nothing in the
// bundle — the actual module is only ever pulled in via the dynamic import in
// the init effect below.
type GetValueFn = typeof import('firebase/remote-config').getValue;
type FetchAndActivateFn = typeof import('firebase/remote-config').fetchAndActivate;
import { useAuth } from './AuthContext';
import { trackEvent } from '../utils/analytics';

// ---------------------------------------------------------------------------
// In-code defaults
// ---------------------------------------------------------------------------
//
// Every experiment id consumed via `useExperiment` MUST be listed here with
// its default variant string. Firebase RC requires defaults to be set BEFORE
// `fetchAndActivate` so the hook never observes `undefined` while loading.
//
// To add a new experiment:
//   1. Add an entry below (key = experiment id, value = default variant).
//   2. Create the matching parameter in Firebase Console → Remote Config.
//   3. Wire the React component with `useExperiment(experimentId, variants)`.
// See `docs/AB_TESTING.md` for the full recipe.
const REMOTE_CONFIG_DEFAULTS: Record<string, string> = {
  paywall_v1_2026_05: 'control',
  // Hero dynamique — matched = hero résolu par segment réel (useVisitorSegment),
  // control = hero ATS unique pour tous. Défaut `matched` pour éviter de perdre
  // la segmentation Phase 1 en cas d'échec du fetch RC. L'A/B se fait via une
  // condition `percent <= 50 → control` superposée dans Firebase A/B Testing.
  landing_hero_dynamic_v1_2026_05: 'matched',
  // /score CTA copy test — hypothesis: framing the CTA as "save your score"
  // (vs. the current "create my free account") reduces the 79% drop on
  // score_complete → sign_up by leading with the saved-value benefit.
  // Variants wired in `src/pages/ScorePage.tsx`. Started 2026-05-14.
  score_cta_v1_2026_05: 'control',
  // Onboarding "Aha-moment" — show CV ATS score immediately after upload,
  // BEFORE job_target/seniority/location/photo. Hypothesis: 77% CV→checkout
  // drop (audit 2026-05-17) caused by surfacing value too late. Treatment
  // inserts a Step_ScoreRevealV3 right after cv_upload. Default control.
  onboarding_score_first_v1_2026_05: 'control',
  // Feature flag (pas un A/B) pour le survey diagnostic CV→checkout.
  // Default 'off' = kill switch immédiat. Active manuellement via Firebase
  // Console quand on veut récolter la data. Survey trop intrusif sans
  // garde-fou, cf. retour Julien 2026-05-17.
  cv_checkout_survey_enabled: 'off',
};

// Cache TTL: aggressive in dev (every fetch hits the network so QA changes
// land instantly) and 1h in prod (Firebase's recommended default — anything
// shorter risks rate-limiting at scale, anything longer slows experiment
// rollouts).
const FETCH_TTL_DEV_MS = 0;
const FETCH_TTL_PROD_MS = 60 * 60 * 1000;

// ---------------------------------------------------------------------------
// URL override (?ab-variant=treatment) — for QA & manual testing
// ---------------------------------------------------------------------------
//
// We accept both `ab-variant` (the new canonical param) and the legacy
// `gb-variant` for backwards compat with bookmarks/links from the previous
// GrowthBook integration. Both behave identically.
const URL_OVERRIDE_PARAM = 'ab-variant';
const URL_OVERRIDE_PARAM_LEGACY = 'gb-variant';

/** Parse `?ab-variant=treatment` (or legacy `?gb-variant=`) into a forced
 *  variant key, or null. */
function readUrlOverride(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const params = new URLSearchParams(window.location.search);
    const v = params.get(URL_OVERRIDE_PARAM) ?? params.get(URL_OVERRIDE_PARAM_LEGACY);
    return v && v.trim().length > 0 ? v.trim() : null;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

interface ExperimentsContextValue {
  /**
   * Read a Remote Config parameter as a string, or null if RC isn't loaded /
   * supported / has no such key. Backed by the dynamically-imported
   * `getValue`; consumers (i.e. `useExperiment`) go through this instead of
   * touching the SDK directly, so the SDK never has to be imported statically.
   */
  getRemoteValue: (experimentId: string) => string | null;
  /** Forced variant from `?ab-variant=...` query param, if any. */
  forcedVariant: string | null;
  /** True when Remote Config has finished its first activate (or skipped it). */
  isReady: boolean;
  /**
   * Internal: called by `useExperiment` once per (experimentId, variant)
   * pair to log the assignment exactly once. Centralised here so the hook
   * itself stays stateless.
   */
  recordAssignment: (
    experimentId: string,
    variant: string,
    extra?: { variantForced?: boolean }
  ) => void;
}

const ExperimentsContext = createContext<ExperimentsContextValue | undefined>(undefined);

export function useExperimentsContext(): ExperimentsContextValue {
  const ctx = useContext(ExperimentsContext);
  if (!ctx) {
    // Soft fallback — never crash, just say we're not ready and have no
    // override. In practice this only happens if `useExperiment` is mounted
    // outside `<ExperimentsProvider>`, which shouldn't occur in our app.
    return {
      getRemoteValue: () => null,
      forcedVariant: null,
      isReady: false,
      recordAssignment: () => {
        /* no-op */
      },
    };
  }
  return ctx;
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

interface ExperimentsProviderProps {
  children: ReactNode;
}

export const ExperimentsProvider: React.FC<ExperimentsProviderProps> = ({ children }) => {
  const { user, loading: authLoading } = useAuth();

  // Remote Config is instantiated lazily on first effect — `getRemoteConfig`
  // touches `window` indirectly so we keep it out of render to stay SSR-safe.
  const rcRef = useRef<RemoteConfig | null>(null);

  // The dynamically-imported SDK helpers we still need after the init effect:
  //   - `getValue`        → read a param on demand (via `getRemoteValue`).
  //   - `fetchAndActivate` → re-fetch when auth state changes.
  // Both are populated once the async `import('firebase/remote-config')`
  // resolves. Until then they stay null and consumers fall back to defaults.
  const getValueRef = useRef<GetValueFn | null>(null);
  const fetchAndActivateRef = useRef<FetchAndActivateFn | null>(null);

  // Track which (experimentId, variant) pairs we've already logged so we
  // don't spam the assignment event on every re-render.
  const loggedAssignmentsRef = useRef<Set<string>>(new Set());

  const [isReady, setIsReady] = useState(false);

  // Initial load of Remote Config. Only attempted if RC is supported.
  // The whole SDK is pulled in lazily here (after first paint) via a dynamic
  // import, so it never lands in the boot chunk. The app renders immediately
  // with in-code defaults; experiments resolve once this resolves.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        // Dynamic import — code-split out of the boot bundle.
        const { fetchAndActivate, getRemoteConfig, getValue, isSupported } =
          await import('firebase/remote-config');
        if (cancelled) return;

        const supported = await isSupported();
        if (!supported || cancelled) {
          if (!cancelled) setIsReady(true);
          return;
        }

        // Stash the helpers we need later (reads + auth-change re-fetches).
        getValueRef.current = getValue;
        fetchAndActivateRef.current = fetchAndActivate;

        const rc = getRemoteConfig(app);
        rc.defaultConfig = REMOTE_CONFIG_DEFAULTS;
        rc.settings.minimumFetchIntervalMillis = import.meta.env.DEV
          ? FETCH_TTL_DEV_MS
          : FETCH_TTL_PROD_MS;
        // Fetch timeout is a separate setting; 10s is conservative for first
        // paint, and we never block on it (defaults cover the loading state).
        rc.settings.fetchTimeoutMillis = 10_000;
        rcRef.current = rc;
        try {
          await fetchAndActivate(rc);
        } catch {
          // Network failure is non-fatal: useExperiment will return defaults.
        }
      } finally {
        if (!cancelled) setIsReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Re-activate Remote Config when the user transitions logged-out → logged-in
  // (or vice versa). Firebase A/B Testing buckets users by the installation
  // id and, post-signup, by the `firebase_user_id` GA4 user property — which
  // is updated automatically by Firebase Auth. So we don't have to push
  // attributes manually; we just trigger one extra fetchAndActivate so any
  // user-property-based targeting kicks in immediately.
  //
  // If the SDK hasn't finished loading yet (rc/fetchAndActivate still null),
  // we skip: the init effect's own fetchAndActivate covers the initial state,
  // and this effect re-runs on the next auth transition anyway.
  useEffect(() => {
    if (authLoading) return;
    const rc = rcRef.current;
    const fetchAndActivate = fetchAndActivateRef.current;
    if (!rc || !fetchAndActivate) return;
    void fetchAndActivate(rc).catch(() => {
      /* swallow — defaults still apply */
    });
  }, [user?.uid, authLoading, isReady]);

  // Read the URL override exactly once on mount — same behaviour as before.
  const forcedVariant = useMemo(() => readUrlOverride(), []);

  // Stable accessor over the dynamically-loaded `getValue`. Reads refs, so it
  // never needs to change identity; consumers re-read after `isReady` flips.
  const getRemoteValue = useMemo<ExperimentsContextValue['getRemoteValue']>(
    () => (experimentId) => {
      const rc = rcRef.current;
      const getValue = getValueRef.current;
      if (!rc || !getValue) return null;
      try {
        return getValue(rc, experimentId).asString();
      } catch {
        return null;
      }
    },
    []
  );

  const recordAssignment = useMemo<ExperimentsContextValue['recordAssignment']>(
    () =>
      (experimentId, variant, extra) => {
        const key = `${experimentId}::${variant}`;
        if (loggedAssignmentsRef.current.has(key)) return;
        loggedAssignmentsRef.current.add(key);
        try {
          trackEvent('experiment_assignment', {
            experiment_id: experimentId,
            variant,
            variant_forced: extra?.variantForced ?? false,
          });
        } catch {
          // Never let analytics break feature delivery.
        }
      },
    []
  );

  const ctxValue = useMemo<ExperimentsContextValue>(
    () => ({
      getRemoteValue,
      forcedVariant,
      isReady,
      recordAssignment,
    }),
    // `getRemoteValue` reads refs (mutated in the init effect) and is itself
    // stable. We re-build the context value whenever `isReady` flips so
    // consumers re-render and re-read the now-resolved Remote Config.
    [forcedVariant, isReady, recordAssignment, getRemoteValue]
  );

  return <ExperimentsContext.Provider value={ctxValue}>{children}</ExperimentsContext.Provider>;
};

export default ExperimentsProvider;
